import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faEdit, faFileCsv } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import jwt_decode from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'
import { DateTime } from 'luxon'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND = '#6eb446'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
  token     : string | null
}
interface DecodedToken { roles: { name: string }[] }
interface Activation {
  device           : string
  device_id?       : string
  ProductId?       : number
  number_activation: number
  createdAt        : string
  deactivated_at?  : string | null
  external_system? : string | null
  Product          : { name: string }
}
interface PackageInfo {
  id          : number
  serial_number: string
  StatusId    : number
  UsertypeId  : number
  Status      : { name: string }
  _partner   ?: { Partner: { name: string } | null }
}
interface LicenseRow {
  id              : number
  product_key     : string
  createdAt       : string
  expireAt        : string
  StatusId        : number
  Status          : { name: string }
  User            : { email: string } | null
  Package         : PackageInfo
  Activations     : Activation[]
  LiveActivations : Activation[]
  license_first   : { createdAt: string }[]
  is_multiplatform: boolean
}
interface Filters {
  partner      : string
  _product_key : string
  products     : string
  useremail    : string
  status       : string
  usertype     : string
}

const INIT_FILTERS: Filters = { partner: '', _product_key: '', products: '', useremail: '', status: '0', usertype: '0' }

const statusBadgeClass = (id: number) =>
  id === 1 ? 'badge text-bg-primary' : id === 2 ? 'badge text-bg-danger' : 'badge text-bg-warning'

// One line per live activation -- for a multiplatform license this shows every
// activated app instead of only the last one (same pattern as Licenses.tsx).
const CellDevice = (row: LicenseRow) => {
  const live = row.LiveActivations ?? []
  if (live.length === 0) return <span className="small">—</span>
  return <>
    {live.map((a, i) => (
      <p className="small mb-1" key={`live-${i}`}>
        <b>{a.device}</b>
        {row.is_multiplatform && <span className="text-muted"> — {a.Product.name}</span>}
        {a.external_system === 'codaly' && <span className="badge text-bg-info ms-1">Codaly</span>}
      </p>
    ))}
  </>
}

const CellCurrentActivation = (row: LicenseRow, locale: string) => {
  const live = row.LiveActivations ?? []
  if (live.length === 0) return <span className="small">—</span>
  return <div className="small">
    {live.map((a, i) => (
      <div className="mb-2" key={`cur-act-${i}`}>
        <p className="mb-0">{DateTime.fromISO(a.createdAt, { zone: 'utc-6' }).setLocale(locale).toFormat('yyyy-MM-dd HH:mm:ss')}</p>
        <p className="mb-0"><b>Product: </b>{a.Product.name}</p>
      </div>
    ))}
  </div>
}

const pill = (bg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  padding: '0.28rem 0.65rem', borderRadius: 9999, backgroundColor: bg,
  color: '#fff', fontWeight: 700, fontSize: '0.75rem', border: 'none',
  cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' as const,
})

const ShowPackage: React.FC = () => {
  const { getHeaders, token } = useContext(AuthContext) as AuthCtx
  const { t, i18n }           = useTranslation()
  const { serial_number }     = useParams<{ serial_number: string }>()
  const paginationComponentOptions = usePaginationComponentOptions()

  const decoded = token ? jwt_decode<DecodedToken>(token) : null
  const isAdmin = decoded?.roles?.[0]?.name === 'admin'

  const downloadRef    = useRef<HTMLButtonElement>(null)
  const downloadClsRef = useRef<HTMLButtonElement>(null)

  const [data, setData]           = useState<LicenseRow[]>([])
  const [loading, setLoading]     = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]     = useState(10)
  const [filters, setFilters]     = useState<Filters>(INIT_FILTERS)
  const [searchOpen, setSearchOpen] = useState(false)

  const buildQuery = (f: Filters) =>
    `&partner=${f.partner}&product_key=${f._product_key}&products=${f.products}&useremail=${f.useremail}&status=${f.status}&usertype=${f.usertype}`

  const fetchLicenses = async (page: number, perPageVal = perPage, f = filters) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: LicenseRow[]; total: number }>(
        `${url}packages/show/${serial_number}?page=${page}&per_page=${perPageVal}${buildQuery(f)}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchLicenses(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchLicenses(page, newPerPage)
  }
  const handleSearch = (values: Filters) => {
    setFilters(values)
    fetchLicenses(1, perPage, values)
  }

  const getLicensesCsv = async (packageId: number) => {
    downloadRef.current?.click()
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}licenses/csv/${packageId}`, { headers })
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]))
      const a       = document.createElement('a')
      a.href        = blobUrl
      a.setAttribute('download', `licenses-${packageId}.csv`)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => downloadClsRef.current?.click(), 500)
    }
  }

  useEffect(() => { fetchLicenses(1) }, [])

  const userTypeLabel: Record<number, string> = {
    1: 'Created by Admin',
    2: 'By partner',
    3: 'By customer',
  }

  const columns = [
    {
      name: t('packages.show.partner'),
      cell: (row: LicenseRow) => (
        <span className="small text-capitalize">
          {row.Package?._partner?.Partner?.name ?? '—'}
        </span>
      ),
    },
    {
      name: t('packages.show.package'),
      cell: (row: LicenseRow) => <span className="small">{row.Package.serial_number}</span>,
    },
    {
      name    : t('packages.show.product_key'),
      minWidth: '270px',
      cell    : (row: LicenseRow) => <span className="small w-100 text-start">{row.product_key}</span>,
    },
    {
      name: t('packages.show.created_at'),
      cell: (row: LicenseRow) => <span className="small">{row.createdAt.slice(0, 19).replace('T', ' ')}</span>,
    },
    {
      name: t('packages.show.email_user_owner'),
      cell: (row: LicenseRow) => <span className="small">{row.User?.email ?? '—'}</span>,
    },
    {
      name: t('packages.show.user_type'),
      cell: (row: LicenseRow) => (
        <span className="small">{userTypeLabel[row.Package.UsertypeId] ?? '—'}</span>
      ),
    },
    {
      name: t('packages.show.current_device'),
      cell: CellDevice,
    },
    {
      name: t('packages.show.number_activation'),
      cell: (row: LicenseRow) => (
        <span className="small">{row.Activations[0]?.number_activation ?? '—'}</span>
      ),
    },
    {
      name: t('packages.show.current_activation'),
      cell: (row: LicenseRow) => CellCurrentActivation(row, i18n.language),
    },
    {
      name: t('packages.show.first_activation'),
      cell: (row: LicenseRow) => {
        const f = row.license_first[0]
        return <span className="small">{f ? f.createdAt.slice(0, 19).replace('T', ' ') : '—'}</span>
      },
    },
    {
      name: t('packages.show.expire_at'),
      cell: (row: LicenseRow) => <span className="small">{row.expireAt?.slice(0, 19).replace('T', ' ') ?? '—'}</span>,
    },
    {
      name: t('packages.show.status'),
      cell: (row: LicenseRow) => {
        if (row.Package.StatusId === 2 || row.Package.StatusId === 5) {
          return (
            <span style={{ fontSize: '0.78rem', color: '#ef4444' }}>
              Package: <b className="badge text-bg-danger">{t(`status.${row.Package.Status.name}`)}</b>
            </span>
          )
        }
        return <span className={`small ${statusBadgeClass(row.StatusId)}`}>{t(`status.${row.Status.name}`)}</span>
      },
    },
    ...(isAdmin ? [{
      name    : t('packages.show.edit'),
      minWidth: '110px',
      cell    : (row: LicenseRow) => (
        <Link to={`/licenses/edit/${row.id}`} style={pill(BRAND)}>
          <FontAwesomeIcon icon={faEdit} />
          <span>{t('packages.show.edit')}</span>
        </Link>
      ),
    }] : []),
  ]

  return (
    <>
      {/* Download modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#dlModalShow" ref={downloadRef} />
      <div className="modal fade" id="dlModalShow" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content" style={{ borderRadius: 16, border: 'none' }}>
            <button type="button" className="d-none" data-bs-dismiss="modal" aria-label="Close" ref={downloadClsRef} />
            <div className="modal-body text-center py-4">
              <div className="spinner-border" role="status" style={{ color: BRAND }}>
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>Generating file…</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-start">

        {/* CSV button */}
        <div className="col-sm-4 col-md-2 mb-3">
          {data.length > 0 && (
            <button
              className="btn btn-primary btn-sm w-100"
              onClick={() => getLicensesCsv(data[0].Package.id)}
            >
              <FontAwesomeIcon icon={faFileCsv} className="me-1" />
              <span className="fw-bold">{t('packages.download_csv')}</span>
            </button>
          )}
        </div>

        {/* Search panel */}
        <div className="col-sm-8 col-md-10 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(p => !p)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.25rem', background: searchOpen ? '#f8fafc' : '#fff',
                border: 'none', borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none',
                cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#475569', textAlign: 'left',
              }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                <Formik initialValues={filters} onSubmit={handleSearch} enableReinitialize>
                  <Form>
                    {[
                      { name: 'partner',      label: t('packages.show.partner') },
                      { name: '_product_key', label: t('packages.show.product_key') },
                      { name: 'products',     label: t('packages.show.compatibility_products') },
                      { name: 'useremail',    label: t('packages.show.email_user_owner') },
                    ].map(f => (
                      <div key={f.name} className="row align-items-center mb-3">
                        <label className="col-sm-3 col-form-label col-form-label-sm">{f.label}</label>
                        <div className="col-sm-9">
                          <Field type="text" className="form-control form-control-sm" name={f.name} placeholder={f.label} />
                        </div>
                      </div>
                    ))}
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-3 col-form-label col-form-label-sm">{t('packages.show.status')}</label>
                      <div className="col-sm-9">
                        <Field as="select" className="form-select form-select-sm" name="status">
                          <option value="0">{t('packages.select_an_option')}</option>
                          <option value="1">{t('status.available')}</option>
                          <option value="2">{t('status.disabled')}</option>
                          <option value="3">{t('status.activated')}</option>
                          <option value="4">{t('status.registered')}</option>
                          <option value="5">{t('status.expired')}</option>
                          <option value="6">{t('status.renovated')}</option>
                        </Field>
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-3 col-form-label col-form-label-sm">{t('packages.show.user_type')}</label>
                      <div className="col-sm-9">
                        <Field as="select" className="form-select form-select-sm" name="usertype">
                          <option value="0">{t('packages.select_an_option')}</option>
                          <option value="1">{t('user_type.admin')}</option>
                          <option value="2">{t('user_type.partner')}</option>
                          <option value="3">{t('user_type.customer')}</option>
                        </Field>
                      </div>
                    </div>
                    <button className="btn btn-sm btn-bd-primary" type="submit">
                      <span className="fw-bold">{t('utilities.search_btn')}</span>
                    </button>
                  </Form>
                </Formik>
              </div>
            )}
          </div>
        </div>

        {/* DataTable */}
        <div className="col-md-12">
          <div className="card card-shadow">
            <DataTable
              columns={columns}
              data={data}
              dense
              fixedHeader
              fixedHeaderScrollHeight="550px"
              progressPending={loading}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              progressComponent={<CustomLoader />}
            />
          </div>
        </div>

      </div>
    </>
  )
}

export default ShowPackage
